import {
  ChartFilter,
  ClubFilter,
  ClubList,
  CompareToggle,
  DetailHeader,
  MembershipStatsCards,
  StatsBanner,
  StatsCard,
  StatsLineChart,
  SubHeadline,
  normalizeClubList,
  statsViewModel,
  useDateRange,
  useFetcher,
  useHectorOneFetcher,
  useSelectedCustomer,
} from "@entrecode/statsapp-components";
import snippets from "@i18n/lang";
import { useLocalStorage } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { CommitmentStatsResponse, OverviewResponse } from "src/*";
import useSWR from "swr";

dayjs.locale("de");

const activationsTooltip = `
  ${snippets.customerDetail.staticCards.tooltips.activations}
  <br /><br />
  ${snippets.customerDetail.staticCards.tooltips.storno}
`;
const cancellationsTooltip = `
  ${snippets.customerDetail.staticCards.tooltips.cancelations}
  <br /><br />
  ${snippets.customerDetail.staticCards.tooltips.terminations}
`;

export default function CustomerDetail() {
  const selectedCustomer = useSelectedCustomer();

  const { start, end, interval } = useDateRange();
  const fetcher = useFetcher();
  const hectorOneFetcher = useHectorOneFetcher();

  const [storedClubs, setStoredClubs] = useLocalStorage(
    `clubList-${selectedCustomer?.seoTitle}`,
    []
  );
  const { clubs: allClubs, isValid: clubListIsValid } = useMemo(
    () => normalizeClubList(storedClubs),
    [storedClubs]
  );

  useEffect(() => {
    if (!clubListIsValid) {
      setStoredClubs([]);
    }
  }, [clubListIsValid, setStoredClubs]);

  const [compareWithPrevious, setCompareWithPrevious] = useState(false);

  const shareable = useRef<HTMLDivElement>(null);

  const query = useMemo(() => {
    if (!selectedCustomer) {
      return "";
    }

    const query = new URLSearchParams({
      dataManagerID: selectedCustomer.dataManagerID,
      start,
      end,
      compare: "year",
      seoTitle: selectedCustomer.seoTitle,
    });

    for (const { clubID, backendID, hidden } of allClubs) {
      if (!hidden) {
        query.append("studioNumbers", `${backendID}$${clubID}`);
      }
    }

    return query.toString();
  }, [selectedCustomer, start, end, allClubs]);

  const {
    data: v2,
    isLoading,
    error,
  } = useSWR(
    selectedCustomer && (start || end) ? `/statsapp/overview?${query}` : null,
    fetcher<OverviewResponse>,
    { keepPreviousData: true }
  );

  const commitmentQuery = useMemo(() => {
    if (!start || !end) {
      return "";
    }
    return new URLSearchParams({
      startDate: dayjs(start).format("YYYY-MM-DD"),
      endDate: dayjs(end).format("YYYY-MM-DD"),
    }).toString();
  }, [start, end]);

  const { data: commitment } = useSWR(
    selectedCustomer && start && end
      ? `/${selectedCustomer.seoTitle}/commitment/stats?${commitmentQuery}`
      : null,
    hectorOneFetcher<CommitmentStatsResponse>,
    { keepPreviousData: true }
  );

  const vm = statsViewModel(v2, {
    start,
    end,
    interval,
    compareWithPrevious,
  });

  return (
    <>
      <Helmet>
        <title>{`${snippets.customerDetail.chartTitle}: ${selectedCustomer?.title}`}</title>
      </Helmet>
      <DetailHeader>
        <div className="flex pr-3 justify-between w-full">
          {selectedCustomer?.title}
          <ClubFilter />
        </div>
      </DetailHeader>
      <div className="px-4">
        <ChartFilter
          shareable={shareable}
          headline={`${snippets.customerDetail.chartTitle}: ${selectedCustomer?.title}`}
        />
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <p className="text-sm text-red-700">
              Die Daten konnten nicht geladen werden. Bitte versuche es später
              erneut.
            </p>
          </div>
        )}
        <CompareToggle
          compareWithPrevios={compareWithPrevious}
          setCompareWithPrevious={setCompareWithPrevious}
          visibleRange={vm.visibleRange}
        />
        {!isLoading && vm.banner === "stale" && (
          <StatsBanner kind="stale" staleUntil={vm.staleUntil} />
        )}
        {!isLoading && vm.banner === "empty" ? (
          <StatsBanner kind="empty" />
        ) : (
          <>
            <div ref={shareable} className="flex flex-col gap-3 mb-8">
              <StatsLineChart data={vm.chartData} />
              <MembershipStatsCards
                core={v2}
                title={selectedCustomer?.title}
                activationsTooltip={activationsTooltip}
                cancellationsTooltip={cancellationsTooltip}
              />
            </div>

            {selectedCustomer?.featureFlags?.["ho-commitmentCenter"]
              ?.clubapp && (
              <div className="my-8">
                {/* Commitment Center Stats */}
                <SubHeadline className="mb-1">Commitment Center</SubHeadline>
                <div className="flex flex-col gap-1">
                  <StatsCard.Double
                    highlight
                    items={[
                      {
                        title: "Neue Mitglieder",
                        sign: "",
                        value: commitment?.totals.onboardings,
                      },
                      {
                        title: "Gesamtzahl Trackings",
                        sign: "",
                        value: commitment?.totals.trackings,
                      },
                      {
                        title: "Aktive Mitglieder",
                        sign: "",
                        value: commitment?.totals.activeMembers,
                      },
                    ]}
                    tooltip={`
                    Neue Mitglieder: Mitglieder die das Commitment Center Onboarding im gewählten Zeitraum abgeschlossen haben.
                    <br /><br />
                    Gesamtzahl Trackings: Gesamtzahl der Trackings im gewählten Zeitraum.
                    <br /><br />
                    Aktive Mitglieder: Mitglieder die im gewählten Zeitraum mindestens ein Tracking abgeschlossen haben.
                  `}
                  />
                </div>
              </div>
            )}
            <ClubList clubs={v2?.clubs} />
          </>
        )}
      </div>
    </>
  );
}
